<template>
    <div
        class="game-card"
        :style="{ '--provider-color': providerColor, borderColor: providerColor }"
        @click="handleLaunch"
    >
        <!-- Provider badge – top center -->
        <div class="provider-badge" :style="{ background: providerColor }">
            {{ providerDisplayName }}
        </div>

        <!-- Full-bleed artwork -->
        <img
            :src="game.image || `https://via.placeholder.com/300x420?text=${encodeURIComponent(game.name)}`"
            :alt="game.name"
            class="game-image"
            @error="setDefaultGameImage"
        />

        <!-- Bottom gradient + title -->
        <div class="game-title-overlay">
            <h3 class="game-title">{{ formattedGameName }}</h3>
        </div>

        <!-- Launching overlay -->
        <div v-if="launching" class="launching-overlay">
            <div class="spinner"></div>
            <p>Launching…</p>
        </div>
    </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';

export default {
    name: 'SlotsGameCard',
    props: {
        game: {
            type: Object,
            required: true
        },
        providerCode: {
            type: String,
            required: true
        }
    },
    data() {
        return {
            launching: false
        };
    },
    computed: {
        ...mapGetters(['authenticated', 'userId']),
        ...mapGetters('slots', ['gameSession', 'favorites']),
        isFavorite() {
            return this.favorites.includes(this.game.code);
        },
        formattedGameName() {
            if (!this.game.name) return '';
            
            let name = this.game.name;
            
            // Replace underscores and hyphens with spaces
            name = name.replace(/[_-]/g, ' ');
            
            // Handle camelCase by adding spaces before capital letters
            name = name.replace(/([a-z])([A-Z])/g, '$1 $2');
            
            // Convert to title case (capitalize first letter of each word)
            name = name.toLowerCase().split(' ').map(word => {
                // Keep common abbreviations in uppercase
                const uppercaseWords = ['rtp', 'vip', 'ii', 'iii', 'iv', 'vs', 'xl', 'xxi', 'xxl'];
                if (uppercaseWords.includes(word.toLowerCase())) {
                    return word.toUpperCase();
                }
                // Capitalize first letter of each word
                return word.charAt(0).toUpperCase() + word.slice(1);
            }).join(' ');
            
            // Clean up multiple spaces
            name = name.replace(/\s+/g, ' ').trim();
            
            return name;
        },
        providerDisplayName() {
            if (!this.providerCode) return '';
            const code = this.providerCode.toUpperCase();
            const providers = {
                'PRAGMATIC': 'Pragmatic Play',
                'PRAGMATIC PLAY': 'Pragmatic Play',
                'PGSOFT': 'PG Soft',
                'HABANERO': 'Habanero',
                'HABNERO': 'Habanero',
                'EVOPLAY': 'Evoplay',
                'REDTIGER': 'Red Tiger',
                'MICROGAMING': 'Microgaming',
                'QUICKSPIN': 'Quickspin',
                'BTG': 'Big Time Gaming',
                'INOUT': 'InOut',
                'CQ9': 'CQ9',
                'GAMEART': 'GameArt',
                'PLAYSON': 'Playson',
                'DREAMTECH': 'DreamTech',
                'GMW': 'GMW',
                'PLAYSTAR': 'Playstar',
                'NAGA': 'Naga',
                'HACKSAW': 'Hacksaw',
                'BGAMING': 'BGaming',
                'NOLIMIT': 'Nolimit',
                'NOLIMITCITY': 'Nolimit',
                'CALETA': 'Caleta',
                'SLOTSGATEWAY': 'Slots Gateway'
            };
            return providers[code] || code.replace(/_/g, ' ');
        },
        providerColor() {
            if (!this.providerCode) return '#7b68ee';
            const code = this.providerCode.toUpperCase();
            const colors = {
                'PRAGMATIC':      '#3d8bff',
                'PRAGMATIC PLAY': '#3d8bff',
                'PGSOFT':         '#ff7c2a',
                'HACKSAW':        '#e91e8c',
                'BGAMING':        '#f5a623',
                'NOLIMIT':        '#ff4500',
                'NOLIMITCITY':    '#ff4500',
                'HABANERO':       '#ff6b35',
                'HABNERO':        '#ff6b35',
                'EVOPLAY':        '#9b59b6',
                'REDTIGER':       '#e74c3c',
                'MICROGAMING':    '#27ae60',
                'QUICKSPIN':      '#1abc9c',
                'BTG':            '#f39c12',
                'INOUT':          '#f1c40f',
                'CALETA':         '#00bcd4',
                'GAMEART':        '#e67e22',
                'PLAYSON':        '#2ecc71',
                'DREAMTECH':      '#8e44ad',
                'CQ9':            '#d35400',
                'GMW':            '#16a085',
                'PLAYSTAR':       '#2980b9',
                'NAGA':           '#c0392b',
                'SLOTSGATEWAY':   '#7b68ee'
            };
            return colors[code] || '#7b68ee';
        }
    },
    methods: {
        ...mapActions('slots', ['launchGameSeamless', 'addFavorite', 'removeFavorite']),
        ...mapActions(['notificationShow']),

        async handleLaunch() {
            if (!this.authenticated) {
                this.notificationShow({ 
                    type: 'error', 
                    message: 'Please login to play' 
                });
                return;
            }

            this.launching = true;

            try {
                const session = await this.launchGameSeamless({
                    providerCode: this.providerCode,
                    gameCode: this.game.code
                });

                if (session) {
                    this.$emit('launch', {
                        provider: this.providerCode,
                        game: this.game.code,
                        session
                    });
                } else {
                    this.notificationShow({ 
                        type: 'error', 
                        message: 'Failed to launch game. Please try again.' 
                    });
                }
            } catch (error) {
                console.error('Failed to launch game:', error);
                this.notificationShow({ 
                    type: 'error', 
                    message: error.message || 'Failed to launch game. Please try again.' 
                });
            } finally {
                this.launching = false;
            }
        },

        async toggleFavorite() {
            if (this.isFavorite) {
                await this.removeFavorite(this.game.code);
            } else {
                await this.addFavorite(this.game.code);
            }
        },

        setDefaultGameImage(event) {
            event.target.src = `data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 300 200"%3E%3Crect fill="%23222" width="300" height="200"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" font-size="16" fill="%23d4a574" font-family="Segoe UI, Tahoma, Geneva, Verdana, sans-serif"%3E${encodeURIComponent(this.game.name)}%3C/text%3E%3C/svg%3E`;
        }
    }
};
</script>

<style scoped>
/* ─── Card shell ─────────────────────────────────────────────────────────── */
.game-card {
    position: relative;
    width: 100%;
    aspect-ratio: 3 / 4;
    border-radius: 14px;
    border: 2.5px solid var(--provider-color, #7b68ee);
    overflow: hidden;
    cursor: pointer;
    background: #111;
    transition: transform 0.22s cubic-bezier(0.34, 1.56, 0.64, 1),
                box-shadow 0.22s ease;
    flex-shrink: 0;
}

.game-card:hover {
    transform: translateY(-6px) scale(1.03);
    box-shadow: 0 0 0 3px var(--provider-color, #7b68ee),
                0 12px 36px rgba(0, 0, 0, 0.6);
}

/* ─── Full-bleed artwork ─────────────────────────────────────────────────── */
.game-image {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center top;
    display: block;
    transition: transform 0.35s ease;
}

.game-card:hover .game-image {
    transform: scale(1.07);
}

/* ─── Provider badge – top center ────────────────────────────────────────── */
.provider-badge {
    position: absolute;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    z-index: 10;
    padding: 4px 10px;
    border-radius: 0 0 8px 8px;
    font-size: 9px;
    font-weight: 800;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #fff;
    white-space: nowrap;
    text-shadow: 0 1px 3px rgba(0, 0, 0, 0.6);
    line-height: 1.3;
}

/* ─── Bottom gradient + game title ───────────────────────────────────────── */
.game-title-overlay {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    padding: 32px 10px 12px;
    background: linear-gradient(to top, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.55) 55%, transparent 100%);
    display: flex;
    align-items: flex-end;
    justify-content: center;
    z-index: 5;
}

.game-title {
    margin: 0;
    font-size: clamp(12px, 1.5vw, 18px);
    font-weight: 900;
    color: #fff;
    text-transform: uppercase;
    text-align: center;
    text-shadow: 0 2px 8px rgba(0, 0, 0, 0.95);
    letter-spacing: 0.03em;
    line-height: 1.15;
    word-break: break-word;
    hyphens: auto;
}

/* ─── Launching overlay ──────────────────────────────────────────────────── */
.launching-overlay {
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.85);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
    z-index: 20;
}

.spinner {
    width: 40px;
    height: 40px;
    border: 3px solid rgba(212, 165, 116, 0.3);
    border-top: 3px solid var(--color-copper, #d4a574);
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
}

@keyframes spin {
    to { transform: rotate(360deg); }
}

.launching-overlay p {
    color: var(--color-copper, #d4a574);
    font-size: 14px;
    font-weight: 600;
    margin: 0;
}

/* ─── Responsive ─────────────────────────────────────────────────────────── */
@media (max-width: 768px) {
    .game-card { border-radius: 11px; }
    .game-title { font-size: 12px; }
    .provider-badge { font-size: 8px; padding: 3px 8px; }
}

@media (max-width: 480px) {
    .game-card { border-radius: 9px; }
    .game-title { font-size: 11px; }
    .provider-badge { font-size: 7px; padding: 3px 7px; }
}
</style>
